<script setup>
import { computed, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SegmentedToggle from 'dashboard/components-next/segmented-toggle/SegmentedToggle.vue';
import { OnClickOutside } from '@vueuse/components';
import CheckboxDropdownMenu from 'dashboard/components-next/dropdown-menu/CheckboxDropdownMenu.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  selectedRows: {
    type: Array,
    default: () => [],
  },
  isSchemaActive: {
    type: Boolean,
    default: false,
  },
  availableColumns: {
    type: Array,
    default: () => [],
  },
  visibleColumns: {
    type: Array,
    default: () => [],
  },
  isDeleting: {
    type: Boolean,
    default: false,
  },
  isTileView: {
    type: Boolean,
    default: false,
  },
  allRowsSelected: {
    type: Boolean,
    default: false,
  },
  libraryType: {
    type: String,
    default: '',
  },
  knowledgebaseId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits([
  'deleteRows',
  'deleteRecords',
  'reindexRows',
  'reindexAllRecords',
  'toggleColumn',
  'previewRow',
  'toggleViewMode',
  'openTemplateConfig',
  'addSite',
  'addPage',
  'addMedia',
  'uploadFile',
]);

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');

const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');

const toolsRoute = computed(() =>
  props.knowledgebaseId
    ? {
        name: 'settings_knowledgebases_tools',
        params: {
          accountId: route.params.accountId,
          knowledgebaseId: String(props.knowledgebaseId),
        },
      }
    : null
);

const showColumnsMenu = ref(false);
const showAddMenu = ref(false);
const showDeleteMenu = ref(false);

const hasSelectedRows = computed(() => props.selectedRows.length > 0);
const selectedRowsCount = computed(() => props.selectedRows.length);
const hasSingleRowSelected = computed(() => props.selectedRows.length === 1);

const hasHiddenColumns = computed(() => {
  if (!props.availableColumns.length || !props.visibleColumns.length) {
    return false;
  }
  return props.visibleColumns.length < props.availableColumns.length;
});

const columnMenuItems = computed(() => {
  return props.availableColumns.map(column => ({
    label: column.headerName || column.field,
    value: column.field,
    action: 'toggle',
    isSelected: props.visibleColumns.includes(column.field),
  }));
});

const addMenuItems = computed(() => [
  {
    label: t('LIBRARIES.SITES.ADD.LABEL'),
    value: 'add-site',
    action: 'create',
    icon: 'i-lucide-globe',
  },
  {
    label: t('LIBRARIES.SITES.ADD_PAGE.LABEL'),
    value: 'add-page',
    action: 'create',
    icon: 'i-lucide-globe',
  },
  {
    label: t('LIBRARIES.RECORDS.UPLOAD.LABEL'),
    value: 'upload-file',
    action: 'upload',
    icon: 'i-lucide-upload',
  },
  {
    label: t('LIBRARIES.RECORDS.ADD_MEDIA.LABEL'),
    value: 'add-media',
    action: 'create',
    icon: 'i-lucide-image-plus',
  },
]);

// Pagination handlers - removed, now handled in DocumentGrid

const handleColumns = () => {
  showColumnsMenu.value = !showColumnsMenu.value;
};

const handleColumnAction = ({ value }) => {
  emit('toggleColumn', value);
  // Don't close the menu so users can toggle multiple columns
};

const deleteMenuItems = computed(() => {
  const uniqueRecordIds = [
    ...new Set(props.selectedRows.map(row => row.record?.id).filter(Boolean)),
  ];
  const recordCount = uniqueRecordIds.length;

  return [
    {
      label: t('LIBRARIES.DOCUMENTS.DELETE_DOCUMENTS_ONLY'),
      value: 'delete-documents',
      action: 'delete-documents',
      icon: 'i-lucide-file-minus',
    },
    {
      label:
        recordCount === 1
          ? t('LIBRARIES.DOCUMENTS.DELETE_SOURCE_SINGLE')
          : t('LIBRARIES.DOCUMENTS.DELETE_SOURCE_MULTIPLE', {
              count: recordCount,
            }),
      value: 'delete-records',
      action: 'delete-records',
      icon: 'i-lucide-trash-2',
    },
  ];
});

const handleDeleteAction = ({ value }) => {
  if (value === 'delete-documents') {
    emit('deleteRows', props.selectedRows);
  } else if (value === 'delete-records') {
    // Extract unique record IDs
    const uniqueRecordIds = [
      ...new Set(props.selectedRows.map(row => row.record?.id).filter(Boolean)),
    ];
    emit('deleteRecords', uniqueRecordIds);
  }
  showDeleteMenu.value = false;
};

const handleReindexRows = () => {
  emit('reindexRows', props.selectedRows);
};

const handleReindexAllRecords = () => {
  emit('reindexAllRecords');
};

const handlePreviewRow = () => {
  if (props.selectedRows.length === 1) {
    emit('previewRow', props.selectedRows[0]);
  }
};

const handleAddAction = async ({ value }) => {
  if (value === 'add-site') emit('addSite');
  else if (value === 'add-page') emit('addPage');
  else if (value === 'upload-file') emit('uploadFile');
  else if (value === 'add-media') emit('addMedia');
  showAddMenu.value = false;
};

const viewModeOptions = computed(() => [
  {
    value: false,
    label: t('LIBRARIES.DOCUMENTS.VIEW.TABLE'),
    icon: 'i-lucide-table',
  },
  {
    value: true,
    label: t('LIBRARIES.DOCUMENTS.VIEW.TILES'),
    icon: 'i-lucide-grid-3x3',
  },
]);
</script>

<template>
  <div
    class="flex items-center justify-between p-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 min-h-[60px]"
  >
    <div v-if="isSchemaActive" class="flex items-center h-8">
      <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
        {{ t('LIBRARIES.DOCUMENTS.SCHEMA.LABEL') }}
      </span>
    </div>

    <!-- Normal Mode: Show all toolbar actions when schema is not active -->
    <template v-else>
      <!-- Selection Actions (visible when rows are selected) -->
      <div v-if="hasSelectedRows" class="flex items-center space-x-4 h-8">
        <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
          {{ selectedRowsCount }}
          {{
            selectedRowsCount === 1
              ? t('LIBRARIES.DOCUMENTS.ROW_SELECTED')
              : t('LIBRARIES.DOCUMENTS.ROWS_SELECTED')
          }}
        </span>

        <!-- Delete Button with Dropdown -->
        <div class="relative">
          <OnClickOutside @trigger="showDeleteMenu = false">
            <NextButton
              icon="i-lucide-trash-2"
              variant="solid"
              color="ruby"
              size="sm"
              :loading="isDeleting"
              :disabled="isDeleting"
              @click="showDeleteMenu = !showDeleteMenu"
            >
              {{ t('LIBRARIES.DOCUMENTS.DELETE_SELECTED') }}
            </NextButton>
            <DropdownMenu
              v-if="showDeleteMenu"
              :menu-items="deleteMenuItems"
              class="mt-2 ltr:left-0 rtl:right-0 top-full"
              @action="handleDeleteAction($event)"
            />
          </OnClickOutside>
        </div>

        <!-- Reindex Button -->
        <NextButton
          icon="i-lucide-refresh-cw"
          variant="solid"
          size="sm"
          @click="handleReindexRows"
        >
          {{ t('LIBRARIES.DOCUMENTS.REINDEX_SELECTED') }}
        </NextButton>

        <!-- Reindex All Button - only show when all rows are selected -->
        <NextButton
          v-if="allRowsSelected"
          icon="i-lucide-refresh-ccw"
          variant="solid"
          size="sm"
          @click="handleReindexAllRecords"
        >
          {{ t('LIBRARIES.RECORDS.REINDEX_ALL.LABEL') }}
        </NextButton>

        <NextButton
          v-if="hasSingleRowSelected"
          icon="i-lucide-eye"
          solid
          size="sm"
          @click="handlePreviewRow"
        >
          {{ t('LIBRARIES.DOCUMENTS.PREVIEW_SELECTED') }}
        </NextButton>
      </div>

      <!-- Default Actions (visible when no rows are selected) -->
      <div v-else class="flex items-center space-x-4 h-8">
        <!-- Add Button -->
        <div class="relative">
          <OnClickOutside @trigger="showAddMenu = false">
            <NextButton
              icon="i-lucide-plus-circle"
              variant="solid"
              size="sm"
              @click="showAddMenu = !showAddMenu"
            >
              {{ t('LIBRARIES.RECORDS.UPLOAD.LABEL') }}
            </NextButton>
            <DropdownMenu
              v-if="showAddMenu"
              :menu-items="addMenuItems"
              class="mt-2 ltr:left-0 rtl:right-0 top-full"
              @action="handleAddAction($event)"
            />
          </OnClickOutside>
        </div>
      </div>
    </template>

    <div class="flex items-center space-x-3 h-8">
      <!-- Template Config Button (only show when not in schema mode and not a gallery) -->
      <div v-if="!isSchemaActive" class="flex items-center space-x-3">
        <NextButton
          v-if="isSuperAdmin && toolsRoute"
          icon="i-lucide-wrench"
          variant="ghost"
          size="sm"
          @click="router.push(toolsRoute)"
        >
          {{ t('LIBRARIES.TOOLS_LABEL') }}
        </NextButton>
        <NextButton
          v-if="libraryType !== 'gallery'"
          icon="i-lucide-layout-template"
          variant="ghost"
          size="sm"
          @click="emit('openTemplateConfig')"
        >
          {{ t('LIBRARIES.TEMPLATE_CONFIG.CONFIGURE') }}
        </NextButton>

        <!-- View Mode Toggle -->
        <SegmentedToggle
          :model-value="isTileView"
          :options="viewModeOptions"
          size="small"
          @update:model-value="emit('toggleViewMode')"
        />
      </div>
    </div>
  </div>
</template>
